import { setCookie } from '../../../utils/common';
import { CONTACT_TYPE_TCOL, MediumContact } from '../types';

export const normalizeDocType = (
  telcoDocType: string,
): { docTypeCode: number; docTypeText: string } => {
  const docTypeObject = {
    docTypeCode: -1,
    docTypeText: null,
  };

  telcoDocType = telcoDocType.replace(/-|_/g, '');

  if (telcoDocType.toLowerCase() === 'nif') {
    docTypeObject.docTypeCode = 1;
    docTypeObject.docTypeText = 'NIF';
  } else if (telcoDocType.toLowerCase() === 'cif') {
    docTypeObject.docTypeCode = 2;
    docTypeObject.docTypeText = 'CIF';
  } else if (telcoDocType.toLowerCase() === 'nie') {
    docTypeObject.docTypeCode = 3;
    docTypeObject.docTypeText = 'NIE';
  } else if (
    telcoDocType.toLowerCase() === 'pasa' ||
    telcoDocType.toLowerCase() === 'pass' ||
    telcoDocType.toLowerCase() === 'pasaporte'
  ) {
    docTypeObject.docTypeCode = 4;
    docTypeObject.docTypeText = 'Pasaporte';
  }
  return docTypeObject;
};

export const normalizeGetMyDataResponse = (
  clientInfo,
  onlyMobilePhone: boolean,
) => {
  const findContacts = (contactsForms, billingDirection, onlyMobilePhone) => {
    let smsContactPhone = {
      contactType: 'SMS',
      value: '',
      contactTypeTCOL: '2',
      favourite: true,
      advertisingUse: false,
      address: {
        id: '',
        locationFullAddress: '',
      },
    };
    let contactPhone;
    let contactEmail = {
      contactType: 'Correo Electrónico',
      value: '',
      contactTypeTCOL: '4',
      favourite: true,
      advertisingUse: false,
      address: {
        id: '',
        locationFullAddress: '',
      },
    };

    let billingDirectionId = '';
    let billingDirectionFullAddress = '';
    if (billingDirection) {
      const { id, locationFullAddress } = billingDirection;
      billingDirectionId = id;
      billingDirectionFullAddress = locationFullAddress;
    }
    const contactAddress = {
      contactId: billingDirectionId,
      value: billingDirectionFullAddress,
    };
    let hasFavoriteSmsContactPhone = false;

    if (contactsForms && Array.isArray(contactsForms)) {
      contactsForms.forEach((elem) => {
        const { contactTypeTCOL, favourite } = elem;
        if (contactTypeTCOL === '1' && favourite) {
          contactPhone = elem;
        } else if (contactTypeTCOL === '2' && favourite) {
          smsContactPhone = elem;
          hasFavoriteSmsContactPhone = true;
        } else if (contactTypeTCOL === '4') {
          if (favourite) {
            contactEmail = elem;
          } else if (!contactEmail.value) {
            contactEmail = elem;
          }
        } else if (contactTypeTCOL === '5' && favourite) {
          contactAddress.contactId = elem.address.id;
          contactAddress.value = elem.address.locationFullAddress;
        }
      });
    }

    const contacts = {
      contactPhone:
        hasFavoriteSmsContactPhone || onlyMobilePhone
          ? smsContactPhone
          : contactPhone || smsContactPhone,
      contactEmail,
      contactAddress,
    };

    return contacts;
  };

  const normalizeCustomerAccounts = (customerAccounts) => {
    let accounts = '';
    if (customerAccounts) {
      accounts = customerAccounts.map((elem) => {
        return {
          customerAccount: elem.spanishIbanAccountNumber,
          customerAccountName: elem.customerAccountName,
          customerAccountId: elem.customerAccountId,
          customerAddress: elem.address,
          customerLegalEntity: elem.legalEntity,
        };
      });
    }
    return accounts;
  };

  const {
    onboardingStatus,
    xcol,
    contactsForms,
    permanentResidence: billingDirection,
    name,
    firstSurName,
    secondSurName,
    companyName,
    documentType,
    numberDocument,
    customerAccounts,
    marketSegmentCode,
  } = clientInfo;

  let { fullName } = clientInfo;

  if (!fullName) {
    fullName = name || '';

    if (firstSurName) {
      fullName = `${fullName} ${firstSurName}`;
    }

    if (secondSurName) {
      fullName = `${fullName} ${secondSurName}`;
    }
  }

  if (xcol) {
    setCookie('XCOL', xcol, 1, '.movistar.es', false);
  }

  const { contactEmail, contactPhone, contactAddress } = findContacts(
    contactsForms,
    billingDirection,
    onlyMobilePhone,
  );

  const { docTypeCode, docTypeText } = normalizeDocType(documentType);

  const normalizedClientInfo = {
    name: name || '',
    firstSurname: firstSurName,
    secondSurname: secondSurName,
    fullName,
    companyName,
    docType: docTypeCode,
    docTypeText,
    docNumber: numberDocument,
    contactEmail,
    contactPhone,
    contactAddress,
    permanentResidence: billingDirection,
    accounts: normalizeCustomerAccounts(customerAccounts),
    additionalInfo: contactsForms,
    xcol,
    marketSegmentCode,
    onboardingStatus,
  };

  return normalizedClientInfo;
};

export const getExistContactType = ({
  additionalContactInfo,
  contactTypeTCOL,
  contact,
}: {
  additionalContactInfo: Array<MediumContact>;
  contactTypeTCOL: CONTACT_TYPE_TCOL;
  contact: MediumContact;
}): MediumContact => {
  // Comprobamos si hay que poner cómo favorito algún tipo de contacto ya existente
  const existingContactType =
    additionalContactInfo &&
    Array.isArray(additionalContactInfo) &&
    additionalContactInfo.find((element) => {
      return (
        element.contactTypeTCOL === contactTypeTCOL &&
        !element.favourite &&
        element.value === contact.value
      );
    });

  return existingContactType ? existingContactType : contact;
};

export const normalizeUpdateMyContactsRequest = ({
  newContactEmail,
  newContactPhone,
  newContactTelf,
  newContactFax,
  newContactAddress,
  additionalInfo,
}: {
  newContactEmail?: MediumContact;
  newContactPhone?: MediumContact;
  newContactTelf?: MediumContact;
  newContactFax?: MediumContact;
  newContactAddress?: MediumContact;
  additionalInfo: Array<MediumContact>;
}): { mediumContacts: Array<MediumContact> } => {
  const mediumContacts = { mediumContacts: [] };

  if (newContactTelf) {
    const telf = getExistContactType({
      additionalContactInfo: additionalInfo,
      contactTypeTCOL: CONTACT_TYPE_TCOL.TELF,
      contact: newContactTelf,
    });

    mediumContacts.mediumContacts.push({ ...telf, favourite: true });
  }

  if (newContactPhone) {
    const phone = getExistContactType({
      additionalContactInfo: additionalInfo,
      contactTypeTCOL: CONTACT_TYPE_TCOL.SMS,
      contact: newContactPhone,
    });

    mediumContacts.mediumContacts.push({ ...phone, favourite: true });
  }

  if (newContactFax) {
    const fax = getExistContactType({
      additionalContactInfo: additionalInfo,
      contactTypeTCOL: CONTACT_TYPE_TCOL.FAX,
      contact: newContactFax,
    });

    mediumContacts.mediumContacts.push({ ...fax, favourite: true });
  }

  if (newContactEmail) {
    const email = getExistContactType({
      additionalContactInfo: additionalInfo,
      contactTypeTCOL: CONTACT_TYPE_TCOL.EMAIL,
      contact: newContactEmail,
    });

    mediumContacts.mediumContacts.push({
      ...email,
      favourite: true,
      advertisingUse: email.advertisingUse || newContactEmail.advertisingUse,
    });
  }

  if (newContactAddress) {
    const address = getExistContactType({
      additionalContactInfo: additionalInfo,
      contactTypeTCOL: CONTACT_TYPE_TCOL.ADDRESS,
      contact: newContactAddress,
    });

    mediumContacts.mediumContacts.push({
      ...address,
      favourite: true,
    });
  }

  return mediumContacts;
};

export const normalizeUpdatePermanentResidenceRequest = ({
  permanentResidenceId,
}: {
  permanentResidenceId: number;
}): { permanentResidence: { id: number } } => {
  return {
    permanentResidence: {
      id: permanentResidenceId,
    },
  };
};
